/**
 * useShellAttackModifiers - the to-hit sum the next attack rolls with, before the map.
 *
 * - The target's own share (v8.368.2): a `called` exposure waives part of a
 *   called shot's penalty, and a `snap` exposure (v8.356.0) refuses aiming at
 *   them. Both come from ONE `toHitExposure` read of the target, so the
 *   fold, the ledger and the attack hook refuse and waive the same things.
 * - `calledShotReduction`: the attacker's talent (Deadeye Shot / Precise
 *   Blow) plus that exposure, capped at 20 - every site that reads the
 *   reduction inherits it. The Controls switch `ignoreCalledShot` (v8.415.0)
 *   makes it the whole 20.
 * - `effectiveCustomMods`: Suppressing Fire's Hard (-20) rides the CUSTOM
 *   list rather than a private term, so the sum, the ledger chip and the
 *   breakdown all show it. An UNAWARE target's Easy (+30) rides it the same
 *   way (v8.440.0), for a target carrying the condition. What Stealth's
 *   ladder says about the scene is added later, where the ladder is known
 *   (`hooks/useSceneUnaware`).
 * - `attackMod`: toggles plus custom mods.
 *
 * Reads `rosterTabs`, not the live-cover `tabs` - the shell builds that one
 * later (see gotchas: a memo above the legacy tab shims cannot read `tabs`).
 * Extracted from DeathWatchSimulator.
 */
import { sideOf } from '../engine/roster/team';
import { useMemo } from 'react';
import { hasWS } from '../engine/weaponSpecials';
import { computeToggleModifier } from '../engine/actions/toggleEntries';
import { toHitExposure } from '../engine/exposures';
import { UNAWARE_MOD, isUnaware } from '../engine/unaware';

const SUPPRESSING_MOD = { id: 'mode-suppressing', label: 'Suppressing Fire (Hard)', value: -20, derived: true };

/** The custom list with the lines the situation adds; the same array when it adds none. */
function derivedMods(customMods, suppressing, targetUnaware) {
  if (!suppressing && !targetUnaware) return customMods;
  return [...customMods, ...(suppressing ? [SUPPRESSING_MOD] : []), ...(targetUnaware ? [UNAWARE_MOD] : [])];
}

/** The target's to-hit exposures against this attacker, or null with no target. */
function targetToHitOf(rosterTabs, activeTab, actor, isMelee) {
  const t = rosterTabs.find((x) => x.id === activeTab)?.target;
  if (!t) return null;
  return toHitExposure(
    { conditions: t.conditions, exposures: t.exposures, teamTag: t.teamTag, sideTag: t.sideTag, size: t.size },
    { teamTag: actor?.sheet?.team?.tag ?? null, sideTag: sideOf(actor?.sheet?.team), isMelee },
  );
}

/** The talent's reduction for this kind of weapon plus the target's, capped at 20; all 20 when the switch ignores it. */
export function calledShotReductionOf(attackerCharStats, isMelee, exposure, ignored = false) {
  if (ignored) return 20;
  const talent = attackerCharStats
    ? attackerCharStats[isMelee ? 'calledShotReductionMelee' : 'calledShotReductionRanged']
    : 0;
  return Math.min(20, (talent || 0) + exposure);
}

/** The toggles' and custom mods' total. */
function attackModOf(a) {
  const specialRules = a.effectiveSlot?.specialRules || [];
  const toggleTotal = computeToggleModifier(
    a.attackToggles,
    a.calledShotReduction,
    a.hasBerserkCharge,
    a.isMelee,
    hasWS(specialRules, 'Inaccurate'),
    specialRules,
    a.targetNoAim,
  );
  return toggleTotal + a.effectiveCustomMods.reduce((sum, m) => sum + m.value, 0);
}

/**
 * @param {object} args - rosterTabs, activeTab, actor, isMelee, effectiveSlot,
 *   attackerCharStats, attackToggles, attackCustomMods, attackMode, ignoreCalledShot.
 * @returns {{ calledShotExposure, calledShotReduction, targetNoAim, hasBerserkCharge, effectiveCustomMods, attackMod }}
 */
export default function useShellAttackModifiers(args) {
  const { rosterTabs, activeTab, actor, isMelee, effectiveSlot, attackerCharStats } = args;
  const { attackToggles, attackCustomMods, attackMode, ignoreCalledShot } = args;
  const targetToHit = useMemo(
    () => targetToHitOf(rosterTabs, activeTab, actor, isMelee),
    [rosterTabs, activeTab, actor, isMelee],
  );
  const calledShotExposure = targetToHit ? targetToHit.calledShotReduction : 0;
  const targetNoAim = targetToHit ? targetToHit.noAim : false;
  const calledShotReduction = calledShotReductionOf(attackerCharStats, isMelee, calledShotExposure, ignoreCalledShot);
  const hasBerserkCharge = attackerCharStats?.hasBerserkCharge ?? false;
  const targetUnaware = useMemo(
    () => isUnaware(rosterTabs.find((x) => x.id === activeTab)?.target),
    [rosterTabs, activeTab],
  );
  const effectiveCustomMods = useMemo(
    () => derivedMods(attackCustomMods, attackMode === 'suppressing', targetUnaware),
    [attackCustomMods, attackMode, targetUnaware],
  );
  const attackMod = useMemo(
    () =>
      attackModOf({
        attackToggles,
        calledShotReduction,
        hasBerserkCharge,
        isMelee,
        effectiveSlot,
        targetNoAim,
        effectiveCustomMods,
      }),
    [attackToggles, effectiveCustomMods, calledShotReduction, hasBerserkCharge, isMelee, effectiveSlot, targetNoAim],
  );
  return { calledShotExposure, calledShotReduction, targetNoAim, hasBerserkCharge, effectiveCustomMods, attackMod };
}
